import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, rupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import QrScanner from "@/components/QrScanner";
import { Package, Plus, PackagePlus, ScanLine, Pencil, Trash2, Download, Upload, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LOW_STOCK = 5;

export default function Products() {
  const { user } = useAuth();
  const isCashier = user?.role === "cashier";
  const [unit, setUnit] = useState(isCashier ? user?.unit || "putra" : "putra");
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [restock, setRestock] = useState(null);
  const [qty, setQty] = useState("");
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [form, setForm] = useState({ name: "", sku: "", price: "", stock: "", category: "Umum" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", sku: "", price: "", category: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);

  const load = (u) => api.get(`/cashier/products?unit=${u}`).then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(unit); }, [unit]);

  const lowStock = products.filter((p) => p.stock <= LOW_STOCK);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/cashier/products/template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "template_barang.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal mengunduh template"); }
  };

  const doImport = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/cashier/products/import?unit=${unit}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.created} barang diimpor ke unit ${unit}`);
      if (r.data.errors?.length) toast.warning(`${r.data.errors.length} baris dilewati`);
      load(unit);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/cashier/products", { ...form, price: parseFloat(form.price), stock: parseInt(form.stock), unit });
      toast.success("Barang ditambahkan");
      setOpen(false);
      setForm({ name: "", sku: "", price: "", stock: "", category: "Umum" });
      load(unit);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doRestock = async () => {
    const q = parseInt(qty);
    if (!q || q <= 0) return toast.error("Jumlah tidak valid");
    try {
      await api.post(`/cashier/products/${restock.id}/restock`, { qty: q });
      toast.success(`Stok ${restock.name} bertambah ${q}`);
      setRestock(null); setQty(""); load(unit);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const onScan = (text) => {
    setScanning(false);
    const code = String(text).trim().toLowerCase();
    const p = products.find((x) => (x.sku || "").toLowerCase() === code || (x.id || "").toLowerCase() === code);
    if (p) { setRestock(p); setQty(""); }
    else toast.error(`Barang dengan kode "${text}" tidak ditemukan di unit ${unit}`);
  };

  const openEdit = (p) => {
    setEditForm({ name: p.name, sku: p.sku || "", price: String(p.price ?? ""), category: p.category || "" });
    setEditing(p);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) return toast.error("Nama barang wajib diisi");
    setEditSaving(true);
    try {
      await api.put(`/cashier/products/${editing.id}`, {
        name: editForm.name.trim(), sku: editForm.sku.trim(),
        price: parseFloat(editForm.price), category: editForm.category.trim(),
      });
      toast.success("Barang diperbarui");
      setEditing(null); load(unit);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setEditSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Hapus barang "${p.name}"? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/cashier/products/${p.id}`);
      toast.success(`Barang "${p.name}" dihapus`);
      load(unit);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        title="Stok Barang"
        subtitle="Kelola produk dan catat barang masuk (restock). Pindai barcode untuk restock cepat."
        action={
          <div className="flex gap-2 flex-wrap">
            <Button onClick={downloadTemplate} data-testid="product-template" variant="outline" className="rounded-xl">
              <Download size={18} className="mr-2" /> Template
            </Button>
            <Button onClick={() => importRef.current?.click()} data-testid="product-import" disabled={importing} variant="outline" className="rounded-xl">
              {importing ? <Loader2 size={18} className="animate-spin mr-2" /> : <Upload size={18} className="mr-2" />} Impor
            </Button>
            <Button onClick={() => setScanning(true)} data-testid="scan-restock-button" variant="outline" className="rounded-xl">
              <ScanLine size={18} className="mr-2" /> Scan Masuk
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-product-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                  <Plus size={18} className="mr-2" /> Tambah Barang
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Tambah Barang Baru</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label>Nama Barang</Label>
                    <Input data-testid="product-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-xl" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>SKU / Kode (Barcode)</Label>
                      <Input data-testid="product-sku-input" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} required className="rounded-xl" />
                    </div>
                    <div className="space-y-2">
                      <Label>Kategori</Label>
                      <Input data-testid="product-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Harga (Rp)</Label>
                      <Input data-testid="product-price-input" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required className="rounded-xl" />
                    </div>
                    <div className="space-y-2">
                      <Label>Stok Awal</Label>
                      <Input data-testid="product-stock-input" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} required className="rounded-xl" />
                    </div>
                  </div>
                  {!isCashier && (
                    <div className="space-y-2">
                      <Label>Unit</Label>
                      <Select value={unit} onValueChange={setUnit}>
                        <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="putra">Kopontren Putra</SelectItem>
                          <SelectItem value="putri">Kopontren Putri</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <DialogFooter>
                    <Button type="submit" disabled={saving} data-testid="save-product-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                      {saving ? "Menyimpan…" : "Simpan"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <input ref={importRef} type="file" accept=".xlsx" className="hidden" onChange={doImport} />

      {lowStock.length > 0 && (
        <div data-testid="low-stock-banner" className="mb-4 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle size={18} className="shrink-0" />
          <span><b>{lowStock.length} barang</b> stok menipis (≤ {LOW_STOCK}): {lowStock.slice(0, 4).map((p) => p.name).join(", ")}{lowStock.length > 4 ? ", …" : ""}. Segera restock.</span>
        </div>
      )}
      {isCashier ? (
        <p className="mb-4 text-sm text-stone-500">Unit: <span className={`font-semibold ${unit === "putra" ? "text-blue-700" : "text-pink-700"}`}>{unit === "putra" ? "Putra" : "Putri"}</span></p>
      ) : (
        <Tabs value={unit} onValueChange={setUnit} className="mb-4">
          <TabsList>
            <TabsTrigger value="putra">Putra</TabsTrigger>
            <TabsTrigger value="putri">Putri</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {products.length === 0 ? (
          <Empty><Package className="mx-auto mb-2" /> Belum ada barang di unit ini.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Nama</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead className="text-right">Harga</TableHead>
                <TableHead className="text-center">Stok</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                  <TableCell className="font-medium text-stone-800">{p.name}</TableCell>
                  <TableCell className="font-mono text-sm">{p.sku}</TableCell>
                  <TableCell className="text-sm text-stone-500">{p.category}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{rupiah(p.price)}</TableCell>
                  <TableCell className="text-center">
                    {p.stock <= LOW_STOCK ? (
                      <span data-testid={`low-badge-${p.id}`} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-700">
                        <AlertTriangle size={11} /> {p.stock}
                      </span>
                    ) : (
                      <span className="font-semibold text-stone-700">{p.stock}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => setRestock(p)} data-testid={`restock-${p.id}`} className="rounded-lg">
                      <PackagePlus size={15} className="mr-1" /> Masuk
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="ml-1">
                      <Pencil size={15} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(p)} data-testid={`del-product-${p.id}`} className="text-rose-600">
                      <Trash2 size={15} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!restock} onOpenChange={(o) => !o && setRestock(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Barang Masuk — {restock?.name}</DialogTitle></DialogHeader>
          <div className="space-y-2 mt-2">
            <Label>Jumlah Masuk</Label>
            <Input type="number" data-testid="restock-qty-input" value={qty} onChange={(e) => setQty(e.target.value)} className="rounded-xl" placeholder="0" />
            <p className="text-xs text-stone-400">Stok saat ini: {restock?.stock}</p>
          </div>
          <DialogFooter>
            <Button onClick={doRestock} data-testid="confirm-restock-button" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">Tambah Stok</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QrScanner open={scanning} onOpenChange={(o) => !o && setScanning(false)} onResult={onScan} title="Pindai Barcode Barang Masuk" />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Edit Barang</DialogTitle></DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Nama Barang</Label>
              <Input data-testid="edit-product-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required className="rounded-xl" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>SKU / Kode (Barcode)</Label>
                <Input data-testid="edit-product-sku" value={editForm.sku} onChange={(e) => setEditForm({ ...editForm, sku: e.target.value })} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Kategori</Label>
                <Input data-testid="edit-product-category" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className="rounded-xl" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Harga (Rp)</Label>
              <Input data-testid="edit-product-price" type="number" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} required className="rounded-xl" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={editSaving} data-testid="save-edit-product" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                {editSaving ? "Menyimpan…" : "Simpan Perubahan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
